import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  jsonLd?: object[];
}

const BASE_URL = "https://unotif.com";
const DEFAULT_TITLE = "Unotif - Notifications push via carte Wallet";
const DEFAULT_DESCRIPTION = "Créez des cartes Wallet mobiles et envoyez des notifications push visibles sur l'écran verrouillé. Sans application, sans SMS, sans email.";
const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/v8WsvNvgs0ZlR2L5Amdm9TPdyqx2/social-images/social-1768482845695-Couverture Unotif.png";

export const SEO = ({ title, description, canonical, noindex = false, jsonLd }: SEOProps) => {
  const fullTitle = title === DEFAULT_TITLE ? title : `${title} | Unotif`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;

  return (
    <Helmet>
      {/* Title */}
      <title>{fullTitle}</title>

      {/* Meta Description */}
      <meta name="description" content={description} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="Unotif" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* JSON-LD Schemas */}
      {jsonLd?.map((schema, index) => (
        <script 
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
};

// Page-specific SEO configurations
export const seoConfig = {
  home: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonical: "/",
  },
  pricing: {
    title: "Tarifs",
    description: "Découvrez les offres Unotif : Gratuit, Starter et Pro. Envoyez des notifications push via carte Wallet à partir de 0€/mois.",
    canonical: "/tarifs",
  },
  blog: {
    title: "Blog",
    description: "Consultez nos conseils, stratégies et informations concernant le marketing Wallet et les notifications push.",
    canonical: "/blog",
  },
  contact: {
    title: "Contact",
    description: "Contactez l'équipe Unotif. Nous répondons à toutes vos questions sur les notifications push via carte Wallet.",
    canonical: "/contact",
  },
  legal: {
    title: "Mentions légales",
    description: "Conditions générales d'utilisation et de vente de la plateforme Unotif.",
    canonical: "/legal",
  },
  privacy: {
    title: "Politique de confidentialité",
    description: "Politique de confidentialité et protection des données personnelles d'Unotif.",
    canonical: "/privacy",
  },
  useCasesCreateurs: {
    title: "Créateurs & Influenceurs",
    description: "Découvrez comment les créateurs et influenceurs utilisent Unotif pour engager leur audience avec des notifications push via Wallet.",
    canonical: "/cas-utilisation/createurs-influenceurs",
  },
  useCasesConferenciers: {
    title: "Conférenciers & Événements",
    description: "Organisez vos événements et conférences avec Unotif : notifications push instantanées via carte Wallet.",
    canonical: "/cas-utilisation/conferenciers-evenements",
  },
  useCasesLocal: {
    title: "Commerces locaux",
    description: "Attirez plus de clients dans votre commerce local grâce aux notifications push géolocalisées via Wallet.",
    canonical: "/cas-utilisation/commerces-locaux",
  },
  useCasesCoachs: {
    title: "Coachs & Communautés",
    description: "Engagez votre communauté et vos membres avec des notifications push personnalisées via carte Wallet.",
    canonical: "/cas-utilisation/coachs-communautes",
  },
  useCasesAgences: {
    title: "Agences",
    description: "Proposez le marketing Wallet à vos clients : une solution premium pour des notifications push sans application.",
    canonical: "/cas-utilisation/agences",
  },
  blogAlternativeEmail: {
    title: "Alternative à l'email marketing en 2026 : comparatif et solutions",
    description: "L'email marketing montre ses limites en 2026. Découvrez les meilleures alternatives (SMS, notifications push, réseaux sociaux) pour communiquer efficacement avec votre audience.",
    canonical: "/alternative-email-marketing",
  },
  affiliationTerms: {
    title: "Conditions du programme d'affiliation",
    description: "Termes et conditions du programme d'affiliation Unotif. Commissions, paiement, pratiques interdites et droit applicable.",
    canonical: "/conditions-affiliation",
  },
  blogOutilsFidelisation: {
    title: "Les meilleurs outils de fidélisation client : guide complet 2026",
    description: "Les 7 meilleurs outils de fidélisation client en 2026 : CRM, programme de fidélité, email, SMS et wallet pour augmenter rétention et ventes.",
    canonical: "/meilleurs-outils-de-fidelisation-client",
  },
  blogProgrammesFidelite: {
    title: "Pourquoi les programmes de fidélité échouent",
    description: "Programme de fidélité ne fonctionne pas ? Identifiez les erreurs clés et appliquez des correctifs simples pour relancer l'engagement et la rétention.",
    canonical: "/programme-fidelite-ne-fonctionne-pas",
  },
  blogNotificationsPushWallet: {
    title: "Notifications push wallet : le canal de fidélisation qui cartonne en 2026",
    description: "Les notifications push wallet révolutionnent la fidélisation client en 2026 : découvrez un canal direct avec fort taux d'ouverture, sans email ni SMS.",
    canonical: "/notifications-push-wallet-fidelisation",
  },
  blogKpiFideliteClient: {
    title: "Comment mesurer la fidélité client : les 6 KPI indispensables",
    description: "Mesurer la fidélité client efficacement : découvrez les 6 KPI indispensables, leurs calculs et comment savoir si vos actions fonctionnent vraiment.",
    canonical: "/mesurer-fidelite-client-kpi",
  },
  blogOutilFidelisationPetitCommerce: {
    title: "Outil de fidélisation pour petit commerce : par où commencer sans gros budget",
    description: "Outil de fidélisation pour petit commerce : découvrez par où commencer sans gros budget avec des solutions simples, efficaces et accessibles dès 0 €.",
    canonical: "/outil-fidelisation-petit-commerce",
  },
  blogFidelisationClientGuide: {
    title: "Comment fidéliser ses clients : le guide complet 2026",
    description: "Fidéliser ses clients efficacement en 2026 : découvrez les stratégies, outils et actions concrètes pour améliorer la rétention et développer votre activité.",
    canonical: "/fidelisation-client-guide",
  },
  blogPourquoiClientsPartent: {
    title: "Pourquoi 68 % des clients partent sans jamais se plaindre",
    description: "Pourquoi 68 % des clients partent en silence ? Comprenez les vraies raisons et comment détecter les signaux pour les retenir à temps.",
    canonical: "/pourquoi-clients-partent",
  },
  blogFidelisationEmotionnelle: {
    title: "Fidélisation émotionnelle vs transactionnelle : laquelle crée une vraie loyauté ?",
    description: "Fidélisation émotionnelle vs transactionnelle : pourquoi les récompenses ne suffisent pas et ce qui pousse réellement vos clients à revenir.",
    canonical: "/fidelisation-emotionnelle-vs-transactionnelle",
  },
  blogClientAmbassadeur: {
    title: "Comment transformer un client satisfait en client ambassadeur",
    description: "Découvrez pourquoi la plupart des clients ne recommandent jamais spontanément et les actions concrètes pour activer ce levier puissant de croissance.",
    canonical: "/client-ambassadeur",
  },
  notFound: {
    title: "Page introuvable",
    description: "La page que vous recherchez n'existe pas ou a été déplacée.",
    noindex: true,
  },
};
